/**
 * App side of the Wallet Standard announce protocol.
 *
 * Extensions such as Phantom, Solflare, and Backpack register themselves through window events:
 * a wallet dispatches `wallet-standard:register-wallet` with a callback that receives the app's
 * `register` function, and listens for `wallet-standard:app-ready` to catch apps that started
 * first. This module keeps the registry; the hook in `index.ts` renders it.
 */
const APP_READY = "wallet-standard:app-ready";
const REGISTER_WALLET = "wallet-standard:register-wallet";
const SOLANA_NAMESPACE = "solana:";
const registry = new Set();
const listeners = new Set();
let snapshot = [];
let started = false;
function publish() {
    snapshot = [...registry];
    for (const notify of listeners)
        notify();
}
function register(...wallets) {
    for (const wallet of wallets)
        registry.add(wallet);
    publish();
    return () => {
        for (const wallet of wallets)
            registry.delete(wallet);
        publish();
    };
}
function start() {
    if (started || typeof window === "undefined")
        return;
    started = true;
    const api = { register };
    window.addEventListener(REGISTER_WALLET, (event) => {
        const callback = event.detail;
        if (typeof callback === "function")
            callback(api);
    });
    window.dispatchEvent(new CustomEvent(APP_READY, { detail: api }));
}
/** Starts listening for wallet announcements and subscribes to registry changes. */
export function subscribeWallets(notify) {
    start();
    listeners.add(notify);
    return () => {
        listeners.delete(notify);
    };
}
/** The announced wallets. Referentially stable between registrations. */
export function walletsSnapshot() {
    return snapshot;
}
/**
 * Whether the wallet serves the requested chains.
 *
 * With no `chains`, any `solana:` chain qualifies: this subpath exists for Solana, and the default
 * keeps EVM-only Wallet Standard wallets out of the list.
 */
export function supportsChains(wallet, chains) {
    if (chains?.length)
        return wallet.chains.some((chain) => chains.includes(chain));
    return wallet.chains.some((chain) => chain.startsWith(SOLANA_NAMESPACE));
}
/** Runs the wallet's `standard:connect` feature. */
export async function connectWallet(wallet) {
    const feature = wallet.features["standard:connect"];
    if (typeof feature?.connect !== "function") {
        throw new Error(`${wallet.name} does not offer standard:connect.`);
    }
    return feature.connect();
}
//# sourceMappingURL=registry.js.map